export const SERVICE_MODE_LOCATED = 'located';
export const SERVICE_MODE_AREA = 'area';

/** Legacy name check: system name or display label identifies barra. */
export function isBarraByName(roomName, roomLabel) {
	const label = (roomLabel ?? '').toLowerCase().trim();
	const name = (roomName ?? '').toLowerCase().trim();
	return label === 'barra' || name === 'barra' || name.includes('barra');
}

/**
 * Effective service mode for a store table row, room object, or reservation-like entity.
 * Mirrors backend VenueRoom::getEffectiveServiceMode().
 */
export function getEffectiveServiceMode(entity) {
	if (!entity) return SERVICE_MODE_LOCATED;
	const explicit = entity.roomServiceMode ?? entity.serviceMode;
	if (explicit) return explicit;
	const roomName = entity.room ?? entity.roomName ?? entity.name ?? '';
	const roomLabel =
		entity.roomLabel ?? entity.roomDisplayName ?? entity.displayName ?? entity.displayRoomName ?? '';
	if (isBarraByName(roomName, roomLabel)) return SERVICE_MODE_AREA;
	return SERVICE_MODE_LOCATED;
}

/** Whether a store table row belongs to a bar room (area or located bar). */
export function isBarRoomTable(table) {
	if (!table) return false;
	const mode = getEffectiveServiceMode(table);
	if (mode === SERVICE_MODE_AREA) return true;
	return mode === SERVICE_MODE_LOCATED && isBarraByName(table.room, table.roomLabel);
}

export function isLocatedBarMode(tables) {
	return (
		Array.isArray(tables) &&
		tables.some((t) => isBarRoomTable(t) && getEffectiveServiceMode(t) === SERVICE_MODE_LOCATED)
	);
}

export function isAreaBarMode(entity) {
	return isBarRoomTable(entity) && getEffectiveServiceMode(entity) === SERVICE_MODE_AREA;
}

export function findBarRoomSystemName(tables) {
	if (!Array.isArray(tables)) return null;
	const barTable = tables.find(isBarRoomTable);
	return barTable?.room ?? null;
}

/** Resolve bar room system name from a list of zone/room names (e.g. zoneReservation). */
export function findBarRoomInZoneList(zones) {
	if (!Array.isArray(zones)) return null;
	if (zones.includes('Barra')) return 'Barra';
	return zones.find((z) => isBarraByName(z)) ?? null;
}

export function isBarSystemRoomName(systemRoomName, tables) {
	if (!systemRoomName) return false;
	if (!tables?.length) return systemRoomName === 'Barra' || isBarraByName(systemRoomName);
	return tables.some((t) => t.room === systemRoomName && isBarRoomTable(t));
}

export function filterBarRoomTables(tables) {
	if (!Array.isArray(tables)) return [];
	return tables.filter(isBarRoomTable);
}

export function isBarRoomReservation(reservation, tables) {
	if (!reservation) return false;
	if (reservation.serviceMode === SERVICE_MODE_AREA) return true;
	if (reservation.serviceMode === SERVICE_MODE_LOCATED) {
		return isBarraByName(reservation.roomName, reservation.roomDisplayName);
	}
	if (reservation.roomName === 'Barra') return true;
	if (tables?.length && reservation.roomName) {
		const match = tables.find((t) => t.room === reservation.roomName);
		if (match) return isBarRoomTable(match);
	}
	return isBarraByName(reservation.roomName, reservation.roomDisplayName);
}

export function isAreaBarReservation(reservation, tables) {
	if (!reservation) return false;
	if (reservation.serviceMode === SERVICE_MODE_AREA) return true;
	if (reservation.serviceMode === SERVICE_MODE_LOCATED) return false;
	return isBarRoomReservation(reservation, tables);
}

export function isLocatedReservation(reservation, tables) {
	if (!reservation) return false;
	if (reservation.serviceMode === SERVICE_MODE_LOCATED) return true;
	if (reservation.serviceMode === SERVICE_MODE_AREA) return false;
	return !isBarRoomReservation(reservation, tables);
}

/**
 * Builds the TPV zone button list (same order as BtnsZones without preference "all").
 */
export function buildTpvZoneList(tables, { includeBarra = true } = {}) {
	if (!Array.isArray(tables) || !tables.length) return [];

	const uniqueZones = Array.from(new Set(tables.map((table) => table.room)));
	const barRoom = findBarRoomSystemName(tables);
	const hasBarra = barRoom != null && uniqueZones.includes(barRoom);
	const filteredOthers = uniqueZones.filter((zone) => zone !== barRoom && zone !== 'Todas');

	const zones = [];
	if (includeBarra && hasBarra) zones.push(barRoom);
	zones.push(...filteredOthers, 'Todas');
	return zones;
}

/**
 * Default zone when opening the TPV table picker for "crear en mesa".
 * Area barra: first dining room (skip barra — use "Nuevo en barra" for bar).
 * Located barra: bar room first (bar is the primary/default room).
 */
export function getDefaultTpvCreateAccountZone(tables, { barraIsLocated, includeBarra = true } = {}) {
	const zones = buildTpvZoneList(tables, { includeBarra });
	if (!zones.length) return findBarRoomSystemName(tables) ?? tables?.[0]?.room ?? null;

	if (barraIsLocated) {
		return zones[0];
	}

	const barRoom = findBarRoomSystemName(tables);
	if (barRoom && zones[0] === barRoom && zones.length > 1) {
		return zones.find((z) => z !== barRoom && z !== 'Todas') ?? zones[1];
	}

	return zones[0];
}
